import * as Globals from "../../globals";
import {defaultRaehmHeight, defaultSchwelleHeight, defaultWallElementHeight} from "../../globals";
import {Box3, BoxGeometry, Mesh, Vector3} from "three";
import {degrees90} from "../../constants/mathConstants";
import {supportBeamMaterial} from "../../constants/materialConstants";
import {pillarMeshName, raehmMeshName, supportBeamMeshName} from "../../constants/nameConstants";
import {addEdgeGeometry, findChildrenByName} from "../../utils/threeUtils";
import {buildingGroup} from "../../main";
import {wallDirectionAxis} from "../walls/wallGeneration";
import {getIntersectingWalls} from "../walls/wall";
import {sortByKey, toFixedFloat, toFixedVector} from "../../utils/utils";
import {PillarPreview, previewYPosition} from "../pillar/pillarPreview";
import {Pillar} from "../pillar/pillar";
import {clearPillarPreviewState} from "../pillar/pillarGeneration";
import {showMessage} from "../../../ui/ui";
import {t} from "../../../i18n";
import {setObjectLayer} from "../../utils/layerUtils";
import {SupportBeamsSpans} from "../../constants/supportBeamConstants";
import {getVolume} from "../../utils/volumeUtils";

export class SupportBeam {
    /**
     * Klasse zur Erstellung eines Unterzugs aus einer Unterzugvorschau.
     * @param supportBeamPreview - Die zuvor erstellte Vorschau des Unterzugs. Breite/Dicke & Position werden hierraus abgeleitet
     * @param height - Höhe des zu erstellten Rähms. Default werden ist die Standard Rähmehöhe wie in den Einstellungen definiert. Kann davon abweichen
     */
    constructor(supportBeamPreview, height = defaultRaehmHeight) {
        this.supportBeamPreview = supportBeamPreview;
        this.width = toFixedFloat(supportBeamPreview.length);
        this.thickness = supportBeamPreview.thickness;
        this.forceEvenPillarAmount = supportBeamPreview.forceEvenPillarAmount !== undefined ? supportBeamPreview.forceEvenPillarAmount : false;
        this.height = height;
        this.outerSupportsAdded = false;
        this.innerSupportsAdded = false;
        this.showWarnings = true;
    }

    create() {
        let beamGeometry = new BoxGeometry(this.width, this.thickness, this.height);
        beamGeometry.rotateX(degrees90); //Rotieren um auf x/z zu liegen
        this.beamMesh = new Mesh(beamGeometry, supportBeamMaterial);
        this.beamMesh.name = supportBeamMeshName;
        this.beamMesh.position.copy(this.supportBeamPreview.secondPreviewMesh.position);
        this.beamMesh.rotation.copy(this.supportBeamPreview.secondPreviewMesh.rotation);
        this.beamMesh.position.y = this.height / 2 + defaultWallElementHeight + defaultSchwelleHeight;
        addEdgeGeometry(this.beamMesh);
        setObjectLayer(this.beamMesh, Globals.layers.supportBeam, true);
        buildingGroup.addWithUuid(this.beamMesh);
        this.bounds = new Box3().setFromObject(this.beamMesh);
        this.orientation = toFixedFloat(this.bounds.getSize(new Vector3()).x) === this.width ? wallDirectionAxis.xAxis : wallDirectionAxis.zAxis;
        this.setUserData();

        //Spannweiten und benötigte Pfosten berechnen
        this.calculateStatics();

        if (this.showWarnings) {
            if (this.outerSupportsAdded) {
                showMessage(t("pillar_placed_end"), "Hinweis");
            }

            if (this.innerSupportsAdded) {
                showMessage(t("pillar_placed_span"), "Hinweis");
            }
        }

        return this.beamMesh;
    }

    setUserData() {
        if (this.beamMesh === null) {
            console.error("Cant set userData of pillar. Element is null");
            return;
        }

        this.beamMesh.userData = {
            width: this.width,
            thickness: this.thickness,
            depth: this.thickness,
            height: this.height,
            originalVolume: getVolume(this.beamMesh.geometry),
            orientation: this.orientation
        };
    }

    calculateStatics() {
        let raehme = findChildrenByName(buildingGroup, raehmMeshName, false, true);
        let pillars = findChildrenByName(buildingGroup, pillarMeshName, false, true);
        let intersectingWalls = getIntersectingWalls(this.beamMesh, raehme, false, false, true); //Geschnittene Wände bestimmen
        let intersectingPillars = [];
        let pillarRanges = [];

        //Get intersecting pillars
        let boundsCopy = new Box3().copy(this.bounds);
        boundsCopy.min.y = 0;
        boundsCopy.max.y = 1;
        boundsCopy.min = toFixedVector(boundsCopy.min);
        boundsCopy.max = toFixedVector(boundsCopy.max);
        for (let i = 0; i < pillars.length; i++) {
            let pillarBounds = new Box3().setFromObject(pillars[i]);
            pillarBounds.min.y = 0;
            pillarBounds.max.y = 1;
            pillarBounds.min = toFixedVector(pillarBounds.min);
            pillarBounds.max = toFixedVector(pillarBounds.max);
            // A post under the beam is a support, even when it is not fully inside the beam.
            if (boundsCopy.intersectsBox(pillarBounds)) {
                intersectingPillars.push(pillars[i]);
                pillarRanges.push(this.getRangeByAxis(pillarBounds));
            }
        }

        let wallRanges = [];
        for (let i = 0; i < intersectingWalls.length; i++) {
            let bounds = new Box3().setFromObject(intersectingWalls[i]);
            let wallRange = this.getRangeByAxis(bounds);
            //Wall range muss begrenze werden auf eine Länge von einer Wanddicke. Sonst wird später bei der Erstellung die ganze Wand eingerechnet und mit Pfosten bestückt
            if (this.orientation === wallDirectionAxis.xAxis) {
                wallRange.start = toFixedFloat(Math.max(wallRange.start, this.bounds.min.x));
                wallRange.finish = toFixedFloat(Math.min(wallRange.finish, this.bounds.max.x));
            } else {
                wallRange.start = toFixedFloat(Math.max(wallRange.start, this.bounds.min.z));
                wallRange.finish = toFixedFloat(Math.min(wallRange.finish, this.bounds.max.z));
            }
            if (toFixedFloat(Math.abs(wallRange.finish - wallRange.start)) >= Globals.HBEWallDefaultThickness - 0.02) {
                wallRanges.push(wallRange);
            }
        }

        // Another beam is a support too. The new beam shares that end instead of growing a second post.
        let beams = findChildrenByName(buildingGroup, supportBeamMeshName, false, true);
        for (let i = 0; i < beams.length; i++) {
            if (beams[i] === this.beamMesh) {
                continue;
            }
            let beamBounds = new Box3().setFromObject(beams[i]);
            beamBounds.min.y = 0;
            beamBounds.max.y = 1;
            beamBounds.min = toFixedVector(beamBounds.min);
            beamBounds.max = toFixedVector(beamBounds.max);
            if (!boundsCopy.intersectsBox(beamBounds)) {
                continue;
            }
            let beamRange = this.getRangeByAxis(beamBounds);
            if (this.orientation === wallDirectionAxis.xAxis) {
                beamRange.start = toFixedFloat(Math.max(beamRange.start, this.bounds.min.x));
                beamRange.finish = toFixedFloat(Math.min(beamRange.finish, this.bounds.max.x));
            } else {
                beamRange.start = toFixedFloat(Math.max(beamRange.start, this.bounds.min.z));
                beamRange.finish = toFixedFloat(Math.min(beamRange.finish, this.bounds.max.z));
            }
            if (toFixedFloat(Math.abs(beamRange.finish - beamRange.start)) >= Globals.HBEWallDefaultThickness - 0.02) {
                wallRanges.push(beamRange);
            }
        }

        let supportBeamRange = this.getRangeByAxis(boundsCopy);
        const support = this.thickness;

        pillarRanges = this.checkEndpointForSupport(supportBeamRange, wallRanges, pillarRanges, supportBeamRange.start, support * 0.5, true);
        pillarRanges = this.checkEndpointForSupport(supportBeamRange, wallRanges, pillarRanges, supportBeamRange.finish, -(support * 0.5), false);

        //Kombinieren und aufsteigen sortieren der Ranges
        let combinedRanges = wallRanges.concat(pillarRanges);
        combinedRanges = sortByKey(combinedRanges, 'start');

        for (let i = combinedRanges.length - 1; i >= 0; i--) {
            let range1 = combinedRanges[i];
            for (let j = combinedRanges.length - 1; j >= 0; j--) {
                let range2 = combinedRanges[j];
                if (i === j) {
                    continue;
                }

                if (range1.start === range2.start && range1.finish === range2.finish) {
                    combinedRanges.splice(i, 1);
                    break;
                }
            }
        }

        if (combinedRanges.length < 2) {
            console.error("Zu wenig Spannposition für Unterzug");
        }

        //Spannweiten zwischen den Ranges
        let spans = [];
        for (let i = 1; i < combinedRanges.length; i++) {
            let span = this.getDistanceBetweenRanges(combinedRanges[i - 1], combinedRanges[i]);
            if (span > 0) {
                spans.push(span);
            }
        }

        //Maximale Spannweite bei aktueller Unterzug Dicke
        let maxSpan = this.getMaxSpanForThickness();
        let maxSingleSpan = maxSpan.single;
        let maxMultiSpan = maxSpan.multi;

        let relevantMaxSpan = spans.length > 1 ? maxMultiSpan : maxSingleSpan;
        let spansInLimit = true;

        for (let i = 0; i < spans.length; i++) {
            if (relevantMaxSpan === null || spans[i] > relevantMaxSpan) {
                spansInLimit = false;
                break;
            }
        }

        if (spansInLimit) {
            return;
        }


        if (!this.forceEvenPillarAmount) {
            if (this.tryAddingPillarToMiddle(combinedRanges, pillarRanges, spans, maxMultiSpan)) {
                return;
            }
        }

        this.addPillarsBySpan(combinedRanges, spans, relevantMaxSpan, maxMultiSpan);
    }

    checkEndpointForSupport(supportBeamRange, wallRanges, pillarRanges, point, offset, isStart) {
        let pointSupported = false;
        for (let i = 0; i < wallRanges.length; i++) {
            if (this.isNearRange(point, wallRanges[i].start, wallRanges[i].finish)) {
                pointSupported = true;
                break;
            }
        }

        if (!pointSupported) {
            for (let i = 0; i < pillarRanges.length; i++) {
                if (this.isNearRange(point, pillarRanges[i].start, pillarRanges[i].finish)) {
                    pointSupported = true;
                    break;
                }
            }
        }

        if (!pointSupported) {
            // The end post is as wide as the beam. Its outer face is the beam end.
            const support = this.thickness;
            let pillarPreview = new PillarPreview(support, support, defaultWallElementHeight + defaultSchwelleHeight);
            let position = new Vector3(0, 0, 0);
            if (this.orientation === wallDirectionAxis.xAxis) {
                position.x = point + offset;
                position.z = this.bounds.getCenter(new Vector3()).z;
            } else {
                position.z = point + offset;
                position.x = this.bounds.getCenter(new Vector3()).x;
            }

            if (isStart) {
                pillarRanges.push({start: point, finish: toFixedFloat(point + support)});
            } else {
                pillarRanges.push({start: toFixedFloat(point - support), finish: point});
            }
            pillarPreview.previewMesh.position.copy(position);
            pillarPreview.previewMesh.position.y = previewYPosition;

            new Pillar(pillarPreview, true).create();
            clearPillarPreviewState();

            this.outerSupportsAdded = true;
        }

        return pillarRanges;
    }

    tryAddingPillarToMiddle(combinedRanges, pillarRanges, spans, maxMultiSpan) {
        //Trivialer Fall, wenn Singleträger ist und die range in eine Multiträger passt. > Mittig neuen Pfosten platzieren und fertig
        if (spans.length === 1) {
            if (spans[0] < maxMultiSpan * 2) {
                let start = combinedRanges[0].start + (0.5 * (combinedRanges[1].start - combinedRanges[0].start));
                let pillarPreview = new PillarPreview(this.thickness, this.thickness, defaultWallElementHeight + defaultSchwelleHeight);
                let position = new Vector3(0, 0, 0);
                if (this.orientation === wallDirectionAxis.xAxis) {
                    position.x = start + (0.5 * this.thickness);
                    position.z = this.bounds.getCenter(new Vector3()).z;
                } else {
                    position.z = start + (0.5 * this.thickness);
                    position.x = this.bounds.getCenter(new Vector3()).x;
                }

                pillarPreview.previewMesh.position.copy(position);
                pillarPreview.previewMesh.position.y = previewYPosition;

                new Pillar(pillarPreview, true).create();
                clearPillarPreviewState();

                this.innerSupportsAdded = true;

                return true;
            }
        }

        return false;
    }

    /**
     * Fügt alle weiteren Pfosten ein, die notwendig sind, um gültige Spannweiten für den Unterzug einzuhalten
     * @param combinedRanges
     * @param spans
     * @param relevantMaxSpan
     * @param maxMultiSpan
     */
    addPillarsBySpan(combinedRanges, spans, relevantMaxSpan, maxMultiSpan) {

        //Ab hier gilt nur noch multi span. Da mindestens ein weiterer Pfosten eingefügt werden muss und dann mehrträgerspannweite gilt
        for (let i = 0; i < spans.length; i++) {
            if (spans[i] > relevantMaxSpan) {
                let requiredPillars = Math.ceil(spans[i] / maxMultiSpan);
                if (this.forceEvenPillarAmount) {
                    if (requiredPillars % 2 !== 0) {
                        requiredPillars++;
                    }
                }
                let spanDistance = spans[i] / (requiredPillars + 1);

                let rangeStart = combinedRanges[i].start;
                // let rangeEnd = combinedRanges[i + 1];

                for (let j = 0; j < requiredPillars; j++) {
                    rangeStart += spanDistance;

                    let pillarPreview = new PillarPreview(this.thickness, this.thickness, defaultWallElementHeight + defaultSchwelleHeight);
                    let position = new Vector3(0, 0, 0);
                    if (this.orientation === wallDirectionAxis.xAxis) {
                        position.x = rangeStart + (0.5 * this.thickness);
                        position.z = this.bounds.getCenter(new Vector3()).z;
                    } else {
                        position.z = rangeStart + (0.5 * this.thickness);
                        position.x = this.bounds.getCenter(new Vector3()).x;
                    }

                    pillarPreview.previewMesh.position.copy(position);
                    pillarPreview.previewMesh.position.y = previewYPosition;

                    new Pillar(pillarPreview, true).create();
                    clearPillarPreviewState();

                    this.innerSupportsAdded = true;
                }
            }
        }
    }

    isInRange(value, start, end) {
        return toFixedFloat(value) >= toFixedFloat(start) && toFixedFloat(value) <= toFixedFloat(end);
    }

    isNearRange(value, start, end) {
        const point = toFixedFloat(value);
        return point >= toFixedFloat(start) - 0.02 && point <= toFixedFloat(end) + 0.02;
    }

    getRangeByAxis(bounds) {
        return this.orientation === wallDirectionAxis.xAxis ?
            {start: toFixedFloat(bounds.min.x), finish: toFixedFloat(bounds.max.x)} :
            {start: toFixedFloat(bounds.min.z), finish: toFixedFloat(bounds.max.z)};
    }

    getDistanceBetweenRanges(range1, range2) {
        return Math.abs(range1.start - range2.start);
    }

    getMaxSpanForThickness() {
        let thickness = this.thickness * 1000;
        let singleMaxSpan = null;
        for (let i = 0; i < SupportBeamsSpans.length; i++) {
            if (SupportBeamsSpans[i].thickness <= thickness) {
                singleMaxSpan = SupportBeamsSpans[i].maxSpan;
            } else {
                break;
            }
        }

        /*let multiMaxSpan = null;
        for (let i = 0; i < RoofThicknessMulti.length; i++) {
            if (RoofThicknessMulti[i].thickness <= thickness) {
                multiMaxSpan = RoofThicknessMulti[i].maxSpan;
            } else {
                break;
            }
        }*/
        return {single: singleMaxSpan, multi: singleMaxSpan};
    }
}
